import { useCallback } from "react";
import { message } from "antd";
import html2pdf from "html2pdf.js";
import { renderQuotation } from "../templates";
import type { PageSize, PageOrientation } from "../templates";

interface QuotationConfig {
  store_name: string;
  address: string;
  phone: string;
  currency: string;
  receipt_footer: string;
  logo_url?: string;
}

interface UseQuotationDocumentParams {
  quotation: any | null;
  config: QuotationConfig;
}

const sanitizeFileName = (value: unknown) => {
  const name = String(value || "quotation")
    .replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")
    .trim();

  return name || "quotation";
};

const removeFrame = (frame: HTMLIFrameElement) => {
  if (frame.parentNode) {
    frame.parentNode.removeChild(frame);
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read PDF blob."));
    reader.readAsDataURL(blob);
  });

export const useQuotationDocument = ({
  quotation,
  config,
}: UseQuotationDocumentParams) => {
  const getQuotationHtml = useCallback(
    (isForPrint = false) => {
      if (!quotation) {
        return "";
      }

      return renderQuotation({
        quotation: {
          id: quotation.id,
          quotation_name: quotation.quotation_name,
          created_at: quotation.created_at,
          customer_id: quotation.customer_id,
          customer_name: quotation.customer_name,
          customer_phone: quotation.customer_phone,
          customer_email: quotation.customer_email,
          customer_address: quotation.customer_address,
          temp_customer_name: quotation.temp_customer_name,
          temp_customer_phone: quotation.temp_customer_phone,
          temp_customer_email: quotation.temp_customer_email,
          temp_customer_address: quotation.temp_customer_address,
          cart_data: quotation.cart_data || [],
          status: quotation.status,
        },
        config: {
          store_name: config.store_name,
          address: config.address,
          phone: config.phone,
          currency: config.currency,
          receipt_footer: config.receipt_footer,
          logo_url: config.logo_url || "",
        },
        pageSize: "A4" as PageSize,
        orientation: "portrait" as PageOrientation,
        isForPrint,
        validityDays: 30,
      });
    },
    [config, quotation],
  );

  const printQuotation = useCallback(() => {
    if (!quotation) {
      return;
    }

    const frame = document.createElement("iframe");
    frame.style.cssText = "position:absolute;top:-9999px;left:-9999px;";
    document.body.appendChild(frame);

    const doc = frame.contentWindow?.document || frame.contentDocument;
    if (!doc) {
      removeFrame(frame);
      return;
    }

    doc.open();
    doc.write(getQuotationHtml(true));
    doc.close();

    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => removeFrame(frame), 1000);
    }, 500);
  }, [getQuotationHtml, quotation]);

  const downloadQuotation = useCallback(() => {
    if (!quotation) {
      return;
    }

    const container = document.createElement("div");
    container.innerHTML = getQuotationHtml(true);

    html2pdf()
      .from(container)
      .set({
        filename: `Quotation_${sanitizeFileName(
          quotation.quotation_name || quotation.id,
        )}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .save();

    message.success("Quotation PDF downloaded!");
  }, [getQuotationHtml, quotation]);

  const getQuotationPdfBase64 = useCallback(async () => {
    if (!quotation) {
      return "";
    }

    const container = document.createElement("div");
    container.innerHTML = getQuotationHtml(true);

    const blob = await html2pdf()
      .from(container)
      .set({
        filename: `Quotation_${sanitizeFileName(
          quotation.quotation_name || quotation.id,
        )}.pdf`,
        margin: 10,
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
        },
      })
      .outputPdf("blob");

    return await blobToBase64(blob);
  }, [getQuotationHtml, quotation]);

  return {
    getQuotationHtml,
    printQuotation,
    downloadQuotation,
    getQuotationPdfBase64,
  };
};
